// SitrepSectionEditDrawer.js

import React, { useEffect, useState, useCallback } from 'react';
import { Offcanvas, Row, Form } from 'react-bootstrap';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import DrawerWrapper, { DrawerFooter } from 'components/IAP/DrawerWrapper';
import FormTextField from 'components/IAP/formFields/FormTextField';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';
import DataSources from 'components/DisasterGPT/DataSources';
import FormCheckboxField from 'components/IAP/formFields/FormCheckboxField';
import DeleteModal from 'components/DesignSystems/DeleteModal';
import { useRefreshSitrepV2 } from './hooks/useSitrepsV2';

const SitrepSectionEditDrawer = ({
  onHide,
  onSave,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  section,
  sitrepId,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      id: '',
      name: '',
      type: 'manual',
      dataSources: [],
      ai_instructions: '',
      output_format_structured_summary_paragraph: false,
      output_format_structured_summary_table: false,
      output_format_structured_summary_list: false,
    },
  });

  const { register, handleSubmit, reset, watch, setValue, getValues, control } =
    methods;
  const selectedType = watch('type');
  const dataSources = useWatch({ control, name: 'dataSources' }) || [];
  const refreshSitrepMutation = useRefreshSitrepV2();

  const handleDataSourcesChange = useCallback(
    (newSelections) => {
      if (typeof newSelections === 'function') {
        // Use getValues to always read the latest form state, avoiding stale closures
        const currentDataSources = getValues('dataSources') || [];
        setValue('dataSources', newSelections(currentDataSources));
      } else {
        setValue('dataSources', newSelections);
      }
    },
    [setValue, getValues]
  );

  const handleTypeToggle = useCallback(
    (isAI) => {
      const newType = isAI ? 'ai' : 'manual';
      setValue('type', newType);

      // Auto-fill AI instructions if toggling to AI and field is empty
      if (isAI) {
        const currentInstructions = getValues('ai_instructions');
        if (!currentInstructions || currentInstructions.trim() === '') {
          setValue('ai_instructions', 'Summarize the information provided');
        }
      }
    },
    [setValue, getValues]
  );

  useEffect(() => {
    if (section) {
      const summaryOptions =
        section?.output_format_structured?.summary_options || [];
      reset({
        id: section.id || '',
        name: section.name || '',
        type: section.type || 'manual',
        dataSources: section.data_sources || [],
        ai_instructions: section.ai_instructions || '',
        output_format_structured_summary_paragraph:
          summaryOptions.includes('Paragraph'),
        output_format_structured_summary_table:
          summaryOptions.includes('Table'),
        output_format_structured_summary_list: summaryOptions.includes('List'),
      });
    } else {
      reset({
        id: '',
        name: '',
        type: 'manual',
        dataSources: [],
        ai_instructions: '',
        output_format_structured_summary_paragraph: false,
        output_format_structured_summary_table: false,
        output_format_structured_summary_list: false,
      });
    }
  }, [section, reset]);

  const handleSaveSectionSettings = async (formData) => {
    const summary_options = [];
    if (formData.output_format_structured_summary_paragraph)
      summary_options.push('Paragraph');
    if (formData.output_format_structured_summary_table)
      summary_options.push('Table');
    if (formData.output_format_structured_summary_list)
      summary_options.push('List');

    const sectionData = {
      id: formData.id,
      name: formData.name,
      type: formData.type,
      data_sources: formData.dataSources,
      ai_instructions: formData.ai_instructions,
      output_format_structured: { summary_options },
    };

    // Call onSave and wait for it to complete
    await onSave(sectionData);

    // If section type is 'ai' and we have a sitrepId, refresh the section
    if (formData.type === 'ai' && sitrepId && formData.id) {
      refreshSitrepMutation.mutate({
        id: sitrepId,
        selectedDatetime: new Date().toISOString(),
        sections: [formData.id],
      });
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onDelete?.();
    setShowDeleteConfirm(false);
    onHide();
  };

  return (
    <DrawerWrapper toggle={onHide} title="Edit Section Settings">
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(handleSaveSectionSettings)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <input type="hidden" {...register('id')} />

            <Row className="mb-3">
              <FormTextField
                title="Section Name"
                fieldName="name"
                register={register}
                rules={{ required: 'Section name is required' }}
              />
            </Row>

            <Row className="mb-3">
              <div className="col-12">
                <label className="form-label">Section Type</label>
                <div className="d-flex align-items-center gap-3">
                  <span
                    className={
                      selectedType === 'manual' ? 'fw-bold' : 'text-muted'
                    }
                  >
                    Manual Entry
                  </span>
                  <Form.Check
                    type="switch"
                    id="section-type-toggle"
                    checked={selectedType === 'ai'}
                    onChange={(e) => handleTypeToggle(e.target.checked)}
                    label=""
                  />
                  <span
                    className={selectedType === 'ai' ? 'fw-bold' : 'text-muted'}
                  >
                    AI Summarized
                  </span>
                </div>
              </div>
            </Row>

            {selectedType === 'ai' && (
              <>
                <Row className="mb-3">
                  <DataSources
                    selections={dataSources}
                    setSelections={handleDataSourcesChange}
                    usingSitrep={true}
                  />
                </Row>

                <Row className="mb-3">
                  <div className="col-12 mt-2">
                    <label
                      className="form-label"
                      htmlFor="ai_instructions-textarea"
                    >
                      AI Instructions
                    </label>
                    <small className="d-block text-muted mb-1">
                      AI interprets and generates search queries
                    </small>
                    <StylishNewTextArea
                      id="ai_instructions-textarea"
                      {...register('ai_instructions')}
                    />
                  </div>
                </Row>

                <Row className="mb-3">
                  <h5>Output Format (Select at least one, or use AI Prompt)</h5>
                  <div className="d-flex flex-column">
                    <FormCheckboxField
                      title="Paragraph"
                      fieldName="output_format_structured_summary_paragraph"
                    />
                    <FormCheckboxField
                      title="Table"
                      fieldName="output_format_structured_summary_table"
                    />
                    <FormCheckboxField
                      title="List"
                      fieldName="output_format_structured_summary_list"
                    />
                  </div>
                </Row>
              </>
            )}
          </Offcanvas.Body>

          <DrawerFooter>
            <div className="button-group">
              {onMoveUp && (
                <StylishNewButton
                  className="button--secondary"
                  onClick={onMoveUp}
                  disabled={!canMoveUp}
                  type="button"
                >
                  Move Up
                </StylishNewButton>
              )}
              {onMoveDown && (
                <StylishNewButton
                  className="button--secondary"
                  onClick={onMoveDown}
                  disabled={!canMoveDown}
                  type="button"
                >
                  Move Down
                </StylishNewButton>
              )}
              {onDelete && (
                <StylishNewButton
                  className="button--tertiary button--reverse"
                  onClick={handleDeleteClick}
                  type="button"
                >
                  Delete Section
                </StylishNewButton>
              )}
              <StylishNewButton className="button--secondary" onClick={onHide}>
                Cancel
              </StylishNewButton>
              <StylishNewButton className="button--primary" type="submit">
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>

      {showDeleteConfirm && (
        <DeleteModal
          title="Delete Section"
          onClose={() => setShowDeleteConfirm(false)}
          onConfirmDelete={handleConfirmDelete}
        >
          <p>Are you sure you want to delete &ldquo;{section?.name}&rdquo;?</p>
          <p className="text-muted small">This action cannot be undone.</p>
        </DeleteModal>
      )}
    </DrawerWrapper>
  );
};

export default SitrepSectionEditDrawer;
